import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import React from 'react';

import {
  CustomTestSections,
  SectionOption,
  Topic
} from '../../redux/Slices/ScheduleTestSlice/types';
import { cn } from '../../utils/tailwindMerge';
import Tooltip from '../Common/ToolTip';

type ScheduleTestSectionsProps = {
  sections: CustomTestSections[];
  onAddSection: () => void;
  onRemoveSection: (index: number) => void;
  onSectionChange: (sectionId: string, index: number) => void;
  onTopicToggle: (topicId: string, index: number) => void;
  onRemoveTopic: (topicId: string, index: number) => void;
  onQuestionsCountChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void;
  onTimeLimitChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void;
  onQuestionsCountArrow: (direction: 'up' | 'down') => (index: number) => void;
  onTimeLimitArrow: (direction: 'up' | 'down') => (index: number) => void;
  topicDropdownOpenIndex: number | null;
  setTopicDropdownOpenIndex: (index: number | null) => void;
  getAvailableSections: (index: number) => SectionOption[];
  getAvailableTopics: (sectionId: string) => Topic[];
  getSelectedTopicNames: (index: number) => string[];
  getTopicTextColor: (index: number) => string;
  getTopicText: (index: number) => string;
  isTopicDropdownOpen?: boolean;
  setIsTopicDropdownOpen?: (isOpen: boolean) => void;
};

const ScheduleTestSections: React.FC<ScheduleTestSectionsProps> = ({
  sections,
  onAddSection,
  onRemoveSection,
  onSectionChange,
  onTopicToggle,
  onRemoveTopic,
  onQuestionsCountChange,
  onTimeLimitChange,
  onQuestionsCountArrow,
  onTimeLimitArrow,
  topicDropdownOpenIndex,
  setTopicDropdownOpenIndex,
  getAvailableSections,
  getAvailableTopics,
  getSelectedTopicNames,
  getTopicTextColor,
  getTopicText,
  isTopicDropdownOpen = false,
  setIsTopicDropdownOpen = () => {
    /*intentionally empty*/
  }
}) => {
  return (
    <div>
      <label className='mb-2 block text-sm font-semibold text-gray-900'>
        Sections <span className='text-[red]'>*</span>
      </label>
      {sections.map((section, index) => (
        <div
          key={section.id}
          className={cn(
            'mb-4 grid w-full grid-cols-1 gap-4 rounded-xl border border-gray-300 p-4 lg:grid-cols-4',
            index > 0 && 'mt-2'
          )}
        >
          {/* Section header with plus and delete icons */}
          <div className='col-span-4 mb-1 flex items-center justify-between'>
            <div className='text-sm font-medium text-gray-700'>
              Section {index + 1}
            </div>
            <div className='flex items-center gap-2'>
              {/* Plus icon for adding sections - appears in all rows */}
              <button
                type='button'
                onClick={onAddSection}
                disabled={sections.length >= 4}
                className='flex items-center justify-center rounded-full p-1 text-blue-600 hover:bg-gray-100 disabled:opacity-50'
                data-tooltip-id='section-tooltip'
                data-tooltip-content={
                  sections.length >= 4
                    ? 'Maximum 4 sections allowed'
                    : 'Add new section'
                }
              >
                <Plus className='h-5 w-5' />
              </button>
              {/* Delete icon - appears in all rows but disabled for last section */}
              <button
                type='button'
                onClick={() => onRemoveSection(index)}
                disabled={sections.length === 1}
                className='flex items-center justify-center rounded-full p-1 text-red-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                data-tooltip-id='section-tooltip'
                data-tooltip-content={
                  sections.length === 1
                    ? 'Minimum 1 section is required'
                    : 'Remove this section'
                }
              >
                <X className='h-5 w-5' />
              </button>
            </div>
          </div>

          <div>
            {/* Section Dropdown */}
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Section <span className='ml-1 text-[red]'>*</span>
              </label>
              <div className='relative'>
                <select
                  value={section.sectionId || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onSectionChange(e.target.value, index)
                  }
                  className='w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <option value=''>Select a section</option>
                  {getAvailableSections(index).map(sectionOption => (
                    <option
                      key={sectionOption.sectionId}
                      value={sectionOption.sectionId}
                    >
                      {sectionOption.sectionName}
                    </option>
                  ))}
                </select>
                <ChevronDown className='pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400' />
              </div>
            </div>
          </div>

          <div>
            {/* Topics Multi-select Dropdown */}
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Topics <span className='ml-1 text-[red]'>*</span>
              </label>
              <div className='relative'>
                <button
                  type='button'
                  onClick={() => {
                    if (section.sectionId) {
                      setTopicDropdownOpenIndex(
                        topicDropdownOpenIndex === index ? null : index
                      );
                      setIsTopicDropdownOpen(topicDropdownOpenIndex !== index);
                    }
                  }}
                  disabled={!section.sectionId}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left',
                    !section.sectionId
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'bg-white hover:bg-gray-50 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500'
                  )}
                >
                  <span className={getTopicTextColor(index)}>
                    {getTopicText(index)}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 text-gray-400 transition-transform',
                      isTopicDropdownOpen && 'rotate-180'
                    )}
                  />
                </button>
                {topicDropdownOpenIndex === index && section.sectionId && (
                  <div className='absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                    {getAvailableTopics(section.sectionId)?.map(
                      (topic: Topic) => (
                        <label
                          key={topic.topicId}
                          className='flex cursor-pointer items-center px-4 py-2 hover:bg-gray-50'
                        >
                          <input
                            type='checkbox'
                            checked={section.topics.includes(topic.topicId)}
                            onChange={() => onTopicToggle(topic.topicId, index)}
                            className='h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                          />
                          <span className='ml-3 text-gray-900'>
                            {topic.topicName}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Selected Topics Display */}
              {section.topics.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-2'>
                  {getSelectedTopicNames(index).map(
                    (topicName: string, topicIndex: number) => (
                      <span
                        key={topicName}
                        className='inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-800'
                      >
                        {topicName}
                        <button
                          type='button'
                          onClick={() =>
                            onRemoveTopic(section.topics[topicIndex], index)
                          }
                          className='ml-2 h-4 w-4 text-indigo-600 hover:text-indigo-800'
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </span>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Number of Questions
                <span className='ml-1 text-[red]'>*</span>
              </label>
              <div className='relative'>
                <input
                  value={
                    section.questionsCount === 0
                      ? ''
                      : section.questionsCount || ''
                  }
                  min='1'
                  max='30'
                  onChange={e => onQuestionsCountChange(e, index)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (e.key === 'ArrowUp') {
                        onQuestionsCountArrow('up')(index);
                      } else if (e.key === 'ArrowDown') {
                        onQuestionsCountArrow('down')(index);
                      }
                    }
                  }}
                  className='w-full appearance-none rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  placeholder='Enter number of questions'
                />
                {/* Stepper arrows */}
                <div className='absolute right-2 top-1/2 flex -translate-y-1/2 flex-col'>
                  {/* Up arrow */}
                  <button
                    type='button'
                    onClick={() => onQuestionsCountArrow('up')(index)}
                    className='flex h-4 w-6 items-center justify-center text-gray-500'
                    disabled={(section.questionsCount || 1) >= 30}
                  >
                    <ChevronUp className='h-5 w-5 text-gray-400' />
                  </button>
                  {/* Down arrow */}
                  <button
                    type='button'
                    onClick={() => onQuestionsCountArrow('down')(index)}
                    className='flex h-4 w-6 items-center justify-center text-gray-500'
                    disabled={(section.questionsCount || 0) <= 0}
                  >
                    <ChevronDown className='h-5 w-5 text-gray-400' />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div>
              <label className='mb-1 block text-sm font-medium text-gray-700'>
                Time Limit (minutes) <span className='ml-1 text-[red]'>*</span>
              </label>
              <div className='relative'>
                <input
                  value={section.timeLimit === 0 ? '' : section.timeLimit || ''}
                  min='1'
                  max='180'
                  onChange={e => onTimeLimitChange(e, index)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (e.key === 'ArrowUp') {
                        onTimeLimitArrow('up')(index);
                      } else if (e.key === 'ArrowDown') {
                        onTimeLimitArrow('down')(index);
                      }
                    }
                  }}
                  className='w-full appearance-none rounded-md border border-gray-300 px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  placeholder='Enter time limit'
                />
                {/* Stepper arrows */}
                <div className='absolute right-2 top-1/2 flex -translate-y-1/2 flex-col'>
                  {/* Up arrow */}
                  <button
                    type='button'
                    onClick={() => onTimeLimitArrow('up')(index)}
                    className='flex h-4 w-6 items-center justify-center text-gray-500'
                    disabled={(section.timeLimit || 1) >= 180}
                  >
                    <ChevronUp className='h-5 w-5 text-gray-400' />
                  </button>
                  {/* Down arrow */}
                  <button
                    type='button'
                    onClick={() => onTimeLimitArrow('down')(index)}
                    className='flex h-4 w-6 items-center justify-center text-gray-500'
                    disabled={(section.timeLimit || 0) <= 0}
                  >
                    <ChevronDown className='h-5 w-5 text-gray-400' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      <Tooltip id='section-tooltip' position='top' />
    </div>
  );
};

export default ScheduleTestSections;
